import { FC, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import EmailTemplateConfigurationModal, {
  EmailTemplateSection,
} from 'dashboard409a/components/EmailTemplateConfigurationModal';
import FileCheckboxList from '../FileCheckboxList';
import {
  COMPANY_NAME_PLACEHOLDER,
  CONTACT_NAME_PLACEHOLDER,
} from '../ProcessReportModal.templates';
import { contactSelectKey, toggleExclusivePath } from '../ProcessReportModal.utils';
import { useProcessReportModalContext } from '../ProcessReportModalContext';
import WorkboardDropboxPicker from '../WorkboardDropboxPicker';

const ProcessReportInputsPanel: FC = () => {
  const {
    companyName,
    useS3Fallback,
    contacts,
    isLoadingContacts,
    selectedContactKey,
    setSelectedContactKey,
    initialPickerPrefix,
    selectedModelPath,
    selectedReportPath,
    setSelectedModelPath,
    setSelectedReportPath,
    modelFolderPath,
    reportFolderPath,
    modelCheckboxFiles,
    reportCheckboxFiles,
    isLoadingModelFiles,
    isLoadingReportFiles,
    emailTemplates,
    emailPresets,
    selectedTemplateId,
    setSelectedTemplateId,
    emailSubject,
    introduction,
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
  } = useProcessReportModalContext();

  const [emailTemplateConfigOpen, setEmailTemplateConfigOpen] = useState(false);

  return (
    <Stack spacing={2}>
      <FormControl size="small" fullWidth>
        <InputLabel id="final-report-contact-label">Final Report Contact</InputLabel>
        <Select
          labelId="final-report-contact-label"
          id="final-report-contact"
          label="Final Report Contact"
          value={selectedContactKey}
          onChange={event => setSelectedContactKey(event.target.value)}
          disabled={isLoadingContacts || contacts.length === 0}
          displayEmpty
          MenuProps={{
            // Nested inside Operations Modal; keep the menu in-tree so clicks register.
            disablePortal: true,
            PaperProps: { sx: { maxHeight: 320 } },
          }}
        >
          {contacts.length === 0 && (
            <MenuItem value="" disabled>
              {isLoadingContacts ? 'Loading contacts…' : 'No contacts found'}
            </MenuItem>
          )}
          {contacts.map((contact, index) => {
            const key = contactSelectKey(contact, index);
            return (
              <MenuItem key={key} value={key}>
                {contact.name}
                {contact.email ? ` (${contact.email})` : ''}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <EmailTemplateSection
        idPrefix="final-report-email"
        templateSelectLabel="Email template"
        templates={emailTemplates}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        emailSubject={emailSubject}
        onConfigure={() => setEmailTemplateConfigOpen(true)}
      />

      <EmailTemplateConfigurationModal
        open={emailTemplateConfigOpen}
        onClose={() => setEmailTemplateConfigOpen(false)}
        idPrefix="final-report-email"
        title="Configure final report email template"
        presets={emailPresets}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        introduction={introduction}
        setIntroduction={setIntroduction}
        introductionPlaceholders={[
          { label: COMPANY_NAME_PLACEHOLDER, token: COMPANY_NAME_PLACEHOLDER },
          { label: CONTACT_NAME_PLACEHOLDER, token: CONTACT_NAME_PLACEHOLDER },
        ]}
        subjectHelperText="Use {{COMPANY_NAME}} and {{CONTACT_NAME}} as needed."
        introductionHelperText="The PDF report name and dashboard URL are added automatically."
        savePresetOpen={savePresetOpen}
        presetName={presetName}
        setPresetName={setPresetName}
        handleOpenSavePreset={handleOpenSavePreset}
        handleCloseSavePreset={handleCloseSavePreset}
        handleSavePreset={handleSavePreset}
        handleApplyPreset={handleApplyPreset}
        handleDeletePreset={handleDeletePreset}
        handleResetToDefault={handleResetToDefault}
      />

      {useS3Fallback ? (
        <Box>
          <Typography variant="subtitle2">Valuation files</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            No valuation folder linked — browse a valuation directory in the backup bucket.
          </Typography>
          <WorkboardDropboxPicker
            key={initialPickerPrefix || 'root'}
            companyName={companyName}
            initialPrefix={initialPickerPrefix}
            selectedModelKey={selectedModelPath}
            selectedReportKey={selectedReportPath}
            onSelectModel={setSelectedModelPath}
            onSelectReport={setSelectedReportPath}
          />
        </Box>
      ) : (
        <>
          <Box>
            <Typography variant="subtitle2">Model Filename</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', wordBreak: 'break-all' }}
            >
              {modelFolderPath}
            </Typography>
            <FileCheckboxList
              files={modelCheckboxFiles}
              selectedPath={selectedModelPath}
              onSelect={path => setSelectedModelPath(prev => toggleExclusivePath(prev, path))}
              isLoading={isLoadingModelFiles}
              emptyLabel="No files found in the Model folder."
              idPrefix="model-file"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2">Final Report File Name</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', wordBreak: 'break-all' }}
            >
              {reportFolderPath}
            </Typography>
            <FileCheckboxList
              files={reportCheckboxFiles}
              selectedPath={selectedReportPath}
              onSelect={path => setSelectedReportPath(prev => toggleExclusivePath(prev, path))}
              isLoading={isLoadingReportFiles}
              emptyLabel="No files found in the Report folder."
              idPrefix="report-file"
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default ProcessReportInputsPanel;
